import React, { useState, useEffect } from 'react';
import axios from 'axios';
import authService from '../../services/authService';
import CustomSelect from '../../components/common/CustomSelect';
import './EarlyEvaluation.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const EarlyEvaluation = () => {
    const [terms, setTerms] = useState([]);
    const [selectedTerm, setSelectedTerm] = useState('');
    const [gradeFilter, setGradeFilter] = useState('ungraded'); // Default: Puanlanmamış
    const [studentTypeFilter, setStudentTypeFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [showEvaluationModal, setShowEvaluationModal] = useState(false);
    const [evaluationData, setEvaluationData] = useState({});
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // Fetch terms on component mount
    useEffect(() => {
        fetchTerms();
    }, []);

    // Fetch students when term or filters change
    useEffect(() => {
        if (selectedTerm) {
            fetchStudents();
        }
    }, [selectedTerm, gradeFilter, studentTypeFilter]);

    const fetchTerms = async () => {
        try {
            const response = await axios.get(`${API_URL}/terms`);
            setTerms(response.data);
            if (response.data.length > 0) {
                setSelectedTerm(response.data[0].id.toString());
            }
        } catch (error) {
            console.error('Error fetching terms:', error);
            setError('Dönemler yüklenirken hata oluştu.');
        }
    };

    const fetchStudents = async () => {
        setLoading(true);
        setError('');
        
        try {
            const user = authService.getCurrentUser();
            if (!user || !user.department) {
                setError('Kullanıcı bölümü bulunamadı.');
                setLoading(false);
                return;
            }

            const params = new URLSearchParams();
            if (gradeFilter !== 'all') params.append('gradeFilter', gradeFilter);
            if (studentTypeFilter !== 'all') params.append('studentTypeFilter', studentTypeFilter);

            const response = await axios.get(
                `${API_URL}/students/${user.department.id}/${selectedTerm}?${params.toString()}`
            );
            setStudents(response.data);
        } catch (error) {
            console.error('Error fetching students:', error);
            setError('Öğrenciler yüklenirken hata oluştu.');
        } finally {
            setLoading(false);
        }
    };



    // Filtreleme
    const filteredStudents = students.filter(student => {
        // İsim araması
        if (searchQuery.trim() !== '') {
            const query = searchQuery.toLowerCase();
            if (!student.name.toLowerCase().includes(query) && 
                !student.studentNumber.includes(query)) {
                return false;
            }
        }
        return true;
    });

    const handleSelectStudent = (studentId) => {
        setSelectedStudents(prev => {
            if (prev.includes(studentId)) {
                return prev.filter(id => id !== studentId);
            } else {
                return [...prev, studentId];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectedStudents.length === filteredStudents.length) {
            setSelectedStudents([]);
        } else {
            setSelectedStudents(filteredStudents.map(s => s.id));
        }
    };

    const handleOpenEvaluationModal = () => {
        if (selectedStudents.length === 0) {
            alert('Lütfen en az bir öğrenci seçin!');
            return;
        }
        
        // Initialize evaluation data for selected students
        const initialData = {};
        selectedStudents.forEach(id => {
            initialData[id] = {
                grade: '',
                reason: ''
            };
        });
        setEvaluationData(initialData);
        setShowEvaluationModal(true);
    };

    const handleEvaluationChange = (studentId, field, value) => {
        setEvaluationData(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                [field]: value
            }
        }));
    };

    const handleCreateReport = async () => {
        // Validate all fields are filled
        const allFilled = selectedStudents.every(id => 
            evaluationData[id]?.grade && evaluationData[id]?.reason
        );

        if (!allFilled) {
            alert('Lütfen tüm öğrenciler için not ve sebep giriniz!');
            return;
        }

        try {
            setLoading(true);
            setError('');
            const user = authService.getCurrentUser();
            const token = localStorage.getItem('token');

            // Prepare bulk grade data
            const grades = selectedStudents.map(studentId => {
                const student = students.find(s => s.id === studentId);
                return {
                    studentId: student.studentNumber,
                    internshipOrder: student.currentInternship.internshipOrder,
                    grade: evaluationData[studentId].grade,
                    gradeComment: evaluationData[studentId].reason
                };
            });

            // First, save the grades
            const gradeResponse = await axios.post(
                `${API_URL}/grade-internships-bulk`,
                { grades },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            console.log('Grades saved:', gradeResponse.data);

            // Get the student IDs (student numbers) of selected students
            const studentIdsToReport = selectedStudents.map(studentId => {
                const student = students.find(s => s.id === studentId);
                return student.studentNumber;
            });

            // Then, generate the report for only the selected students
            const reportResponse = await axios.post(
                `${API_URL}/internship/generate-report`,
                {
                    termId: parseInt(selectedTerm),
                    departmentId: user.department.id,
                    gradeFilter: null,
                    studentTypeFilter: null,
                    studentIds: studentIdsToReport
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    responseType: 'blob'
                }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([reportResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            
            const termName = terms.find(t => t.id.toString() === selectedTerm)?.name || 'Donem';
            link.setAttribute('download', `Erken_Degerlendirme_Tutanagi_${termName.replace(/\s/g, '_')}.docx`);
            
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            // Close modal and show success
            setShowEvaluationModal(false);
            setShowSuccessMessage(true);
            setSelectedStudents([]);
            setEvaluationData({});

            // Refresh student list
            fetchStudents();

            // Hide success message after 3 seconds
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, 3000);
        } catch (error) {
            console.error('Error creating report:', error);
            
            // Handle blob error responses
            if (error.response && error.response.data instanceof Blob) {
                try {
                    const text = await error.response.data.text();
                    const errorData = JSON.parse(text);
                    setError(errorData.error || 'Değerlendirme kaydedilirken veya rapor oluşturulurken hata oluştu.');
                } catch (parseError) {
                    setError('Değerlendirme kaydedilirken veya rapor oluşturulurken hata oluştu.');
                }
            } else if (error.response && error.response.data && error.response.data.error) {
                setError(error.response.data.error);
            } else {
                setError('Değerlendirme kaydedilirken veya rapor oluşturulurken hata oluştu.');
            }
        } finally {
            setLoading(false);
        }
    };

    const getSelectedStudentData = () => {
        return selectedStudents.map(id => 
            students.find(s => s.id === id)
        ).filter(s => s !== undefined);
    };

    return (
        <div>
            <h2>Erken Değerlendirme</h2>
            <p>Staj sürecini erken tamamlayan öğrencileri değerlendirin ve raporlayın.</p>

            {/* Error Message */}
            {error && <div className="error-message" style={{ marginBottom: '20px' }}>{error}</div>}

            {/* Success Message */}
            {showSuccessMessage && (
                <div className="success-banner">
                    <i className="fa-solid fa-circle-check"></i>
                    Erken değerlendirme tutanağı başarıyla oluşturuldu!
                </div>
            )}

            {/* Filtreler ve Arama */}
            <div className="filters-card">
                <div className="filter-row">
                    <div className="filter-group">
                        <label>Dönem Seçiniz:</label>
                        <CustomSelect
                            options={terms.length === 0 
                                ? [{ value: '', label: 'Dönem bulunamadı' }] 
                                : terms.map(term => ({ value: term.id.toString(), label: term.name }))
                            }
                            value={selectedTerm}
                            onChange={(val) => setSelectedTerm(val)}
                            disabled={loading || terms.length === 0}
                            placeholder="Dönem Seçiniz"
                        />
                    </div>

                    <div className="filter-group">
                        <label>Puana Göre Filtrele:</label>
                        <CustomSelect
                            options={[
                                { value: 'all', label: 'Tümü' },
                                { value: 'S', label: 'Başarılı (S)' },
                                { value: 'U', label: 'Başarısız (U)' },
                                { value: 'ungraded', label: 'Puanlanmamış' }
                            ]}
                            value={gradeFilter}
                            onChange={(val) => setGradeFilter(val)}
                            disabled={loading}
                        />
                    </div>

                    <div className="filter-group">
                        <label>Staj Tipi:</label>
                        <CustomSelect
                            options={[
                                { value: 'all', label: 'Tümü' },
                                { value: 'first', label: 'Zorunlu Staj 1' },
                                { value: 'second', label: 'Zorunlu Staj 2' }
                            ]}
                            value={studentTypeFilter}
                            onChange={(val) => setStudentTypeFilter(val)}
                            disabled={loading}
                        />
                    </div>
                </div>

                {/* Arama Çubuğu */}
                <div className="search-bar">
                    <i className="fa-solid fa-search"></i>
                    <input 
                        type="text"
                        placeholder="Öğrenci adı veya numarası ile ara..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="clear-search" onClick={() => setSearchQuery('')}>
                            <i className="fa-solid fa-times"></i>
                        </button>
                    )}
                </div>
            </div>

            {/* Öğrenci Listesi Tablosu */}
            <div className="table-container-card">
                <div className="table-header-info">
                    <h3>{terms.find(t => t.id.toString() === selectedTerm)?.name || 'Seçili Dönem'} Dönemi - Erken Değerlendirme</h3>
                    <span className="student-count">
                        {loading ? 'Yükleniyor...' : `${filteredStudents.length} öğrenci`}
                    </span>
                </div>
                
                {selectedStudents.length > 0 && (
                    <div className="selection-info">
                        <i className="fa-solid fa-check-circle"></i>
                        {selectedStudents.length} öğrenci seçildi
                    </div>
                )}

                <div className="table-wrapper">
                    <table className="styled-table">
                        <thead>
                            <tr>
                                <th style={{width: '4%'}}>
                                    <input 
                                        type="checkbox"
                                        checked={filteredStudents.length > 0 && selectedStudents.length === filteredStudents.length}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th style={{width: '10%'}}>Numara</th>
                                <th style={{width: '13%'}}>Ad Soyad</th>
                                <th style={{width: '15%'}}>Önceki Staj Yeri</th>
                                <th style={{width: '15%'}}>Mevcut Staj Yeri</th>
                                <th style={{width: '12%'}}>Başlangıç</th>
                                <th style={{width: '12%'}}>Bitiş</th>
                                <th style={{width: '10%'}}>Staj Notu</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center', padding: '30px' }}>
                                        <i className="fa-solid fa-spinner fa-spin"></i> Yükleniyor...
                                    </td>
                                </tr>
                            ) : filteredStudents.length === 0 ? (
                                <tr>
                                    <td colSpan="8" style={{ textAlign: 'center', padding: '30px' }}>
                                        <i className="fa-solid fa-circle-info"></i> Seçili filtrelere uygun öğrenci bulunamadı.
                                    </td>
                                </tr>
                            ) : (
                                filteredStudents.map(student => {
                                    if (!student.currentInternship) return null;
                                    
                                    return (
                                        <tr key={student.id}>
                                            <td>
                                                <input 
                                                    type="checkbox"
                                                    checked={selectedStudents.includes(student.id)}
                                                    onChange={() => handleSelectStudent(student.id)}
                                                />
                                            </td>
                                            <td>{student.studentNumber}</td>
                                            <td>{student.name}</td>
                                            <td>
                                                {student.previousInternship 
                                                    ? student.previousInternship.company 
                                                    : <span className="no-data">-</span>
                                                }
                                            </td>
                                            <td>{student.currentInternship.company}</td>
                                            <td>{new Date(student.currentInternship.startDate).toLocaleDateString('tr-TR')}</td>
                                            <td>{new Date(student.currentInternship.endDate).toLocaleDateString('tr-TR')}</td>
                                            <td>
                                                <span className={`grade-badge ${student.currentInternship.grade ? `grade-${student.currentInternship.grade.toLowerCase()}` : 'grade-ungraded'}`}>
                                                    {student.currentInternship.grade || '-'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
                
                <div className="table-footer">
                    <button 
                        className="btn-early-evaluate"
                        onClick={handleOpenEvaluationModal}
                        disabled={loading || selectedStudents.length === 0}
                    >
                        <i className="fa-solid fa-clock-rotate-left"></i> 
                        Erken Değerlendir ({selectedStudents.length})
                    </button>
                </div>
            </div>

            {/* Evaluation Modal */}
            {showEvaluationModal && (
                <div className="modal-overlay" onClick={() => setShowEvaluationModal(false)}>
                    <div className="modal-content evaluation-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Erken Değerlendirme Formu</h3>
                            <button className="modal-close" onClick={() => setShowEvaluationModal(false)}>
                                <i className="fa-solid fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="modal-body">
                            <p className="modal-description">
                                Seçilen öğrenciler için erken değerlendirme notlarını ve sebeplerini girin.
                            </p>

                            <div className="evaluation-list">
                                {getSelectedStudentData().map(student => (
                                    <div key={student.id} className="evaluation-item">
                                        <div className="evaluation-student-info">
                                            <strong>{student.name}</strong>
                                            <span className="student-number">({student.studentNumber})</span>
                                        </div>
                                        
                                        <div className="evaluation-inputs">
                                            <div className="eval-input-group">
                                                <label>Not:</label>
                                                <CustomSelect
                                                    options={[
                                                        { value: 'S', label: 'S (Başarılı)' },
                                                        { value: 'U', label: 'U (Başarısız)' }
                                                    ]}
                                                    value={evaluationData[student.id]?.grade || ''}
                                                    onChange={(val) => handleEvaluationChange(student.id, 'grade', val)}
                                                    placeholder="Seçiniz"
                                                />
                                            </div>
                                            
                                            <div className="eval-input-group">
                                                <label>Sebep:</label>
                                                <input 
                                                    type="text"
                                                    placeholder="Erken değerlendirme sebebi..."
                                                    value={evaluationData[student.id]?.reason || ''}
                                                    onChange={(e) => handleEvaluationChange(student.id, 'reason', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className="modal-footer">
                            <button className="btn-modal-cancel" onClick={() => setShowEvaluationModal(false)} disabled={loading}>
                                İptal
                            </button>
                            <button className="btn-modal-confirm" onClick={handleCreateReport} disabled={loading}>
                                {loading ? (
                                    <>
                                        <i className="fa-solid fa-spinner fa-spin"></i> İşleniyor...
                                    </>
                                ) : (
                                    <>
                                        <i className="fa-solid fa-file-signature"></i> Değerlendirme Tutanağı Oluştur
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default EarlyEvaluation;
